import { useEffect, useRef } from "react";

import { InteractionStatus } from "@hightouch/lib/llm/schema";

import { useAgentSessionLatestTurnQuery } from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";

type Turn = { id: string; status: string };

/**
 * Calls `onStart` when a new turn starts in the session and `onEnd` when one
 * finishes, however it was started. Turns under way or done when it mounts,
 * or when it moves to another session, call neither.
 */
export function useSessionTurns(
  sessionId: string | null | undefined,
  { onStart, onEnd }: { onStart?: () => void; onEnd?: () => void },
) {
  const { refetchInterval } = useGovernedRefetchInterval<Turn | null>({
    baseMs: 3000,
    maxMs: 10000,
    compareKey: (turn) => (turn ? [turn.id, turn.status] : null),
  });
  const { data: turn } = useAgentSessionLatestTurnQuery(
    { sessionId: sessionId ?? "" },
    {
      enabled: Boolean(sessionId),
      select: (data) => data.llm_chat_interactions[0] ?? null,
      refetchInterval,
    },
  );

  const callbacks = useRef({ onStart, onEnd });
  callbacks.current = { onStart, onEnd };
  const previous = useRef<{ sessionId: string; turn: Turn | null }>();
  useEffect(() => {
    if (turn === undefined || !sessionId) return;
    const before =
      previous.current?.sessionId === sessionId
        ? previous.current.turn
        : undefined;
    previous.current = { sessionId, turn };
    if (before === undefined || !turn) return;
    const isRunning = turn.status === InteractionStatus.Pending;
    if (before === null || turn.id !== before.id) {
      callbacks.current.onStart?.();
      // A successor can start before polling observes the previous turn's completion.
      if (!isRunning || before?.status === InteractionStatus.Pending)
        callbacks.current.onEnd?.();
    } else if (before.status === InteractionStatus.Pending && !isRunning) {
      callbacks.current.onEnd?.();
    }
  }, [sessionId, turn]);
}
